// ---------------------------------------------------------------------------
// ScrollTypewriter.js
// ---------------------------------------------------------------------------
// Runs the existing <Typewriter /> component but ONLY after the element
// scrolls into view. Before it hits the viewport it renders the finished
// text invisibly (opacity-0) so the surrounding layout stays stable — no
// height jump when the effect starts.
//
// Usage:
//   <ScrollTypewriter
//     as='h2'
//     className='font-display text-3xl md:text-4xl font-bold text-center'
//     segments={[{ text: 'Everything in one platform', className: 'jfk-crimson' }]}
//     speed={22}
//   />
//
// Options mirror the Typewriter API. IntersectionObserver uses a rootMargin
// of 0px 0px -12% 0px so the animation begins just as the heading enters
// the viewport (not the moment its top pixel appears).
// ---------------------------------------------------------------------------
import React, { useEffect, useRef, useState } from 'react';
import { Typewriter } from './Typewriter';

export const ScrollTypewriter = ({
  segments = [],
  className = '',
  as = 'h2',
  speed = 22,
  startDelay = 120,
  once = true,
  id,
  ...rest
}) => {
  const wrapperRef = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback: no IO support → start immediately.
      setStarted(true);
      return undefined;
    }
    // Respect reduced-motion — skip the animation and show the final text.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setStarted(true); return undefined; }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            if (once) obs.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);

  // Full text as a plain, non-animated string — used as a hidden placeholder
  // BEFORE the animation starts so the container reserves its final height
  // (prevents the "jump" when the typewriter kicks in).
  const fullText = segments.map((s) => s.text).join('');

  return (
    <div ref={wrapperRef} id={id} className='inline-block'>
      {started ? (
        <Typewriter
          as={as}
          segments={segments}
          className={className}
          speed={speed}
          startDelay={startDelay}
          {...rest}
        />
      ) : (
        // Invisible placeholder so line-height / word-wrap match the eventual
        // typewriter output. aria-hidden because we don't want screen readers
        // to announce the placeholder + then the live typewriter twice.
        React.createElement(
          as,
          {
            className: `${className} opacity-0`,
            'aria-hidden': 'true',
          },
          fullText,
        )
      )}
    </div>
  );
};

export default ScrollTypewriter;
